"use client";

import IconTile from "@/components/ui/icon-tile";
import { ArrowLeft, ArrowRight, Sparkles, X } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";

// Confident ease-out-expo — decisive deceleration, no dated bounce.
const EASE_OUT_EXPO = [0.16, 1, 0.3, 1];
const GLIDE = "320ms cubic-bezier(0.16,1,0.3,1)";

/**
 * Reusable Feature Announcement Component.
 *
 * Single-step (backward compatible):
 *   <FeatureAnnouncement targetRef={ref} title="…" description="…" isOpen onClose={…} />
 *
 * Multi-step guided tour:
 *   <FeatureAnnouncement isOpen onClose={…} steps={[{ targetRef, title, description, position }, …]} />
 *
 * @param {object} props
 * @param {React.RefObject} [props.targetRef] - Single-step target to highlight
 * @param {string} [props.title] - Single-step title
 * @param {string} [props.description] - Single-step description
 * @param {Array}  [props.steps] - Multi-step tour: [{ targetRef, title, description, position?, eyebrow? }]
 * @param {boolean} props.isOpen - Whether to show the announcement
 * @param {function} props.onClose - Called when the user finishes or dismisses
 * @param {string} [props.buttonText] - Label for the final action (default: "Got it")
 * @param {string} [props.position] - Default card placement: "top"|"bottom"|"left"|"right" (default: "bottom")
 * @param {string} [props.eyebrow] - Default eyebrow label (default: "What's new")
 */
export default function FeatureAnnouncement({
  targetRef,
  title,
  description,
  steps,
  isOpen,
  onClose,
  buttonText = "Got it",
  position = "bottom",
  eyebrow = "What's new",
}) {
  const prefersReduced = useReducedMotion();
  const cardRef = useRef(null);

  const [targetRect, setTargetRect] = useState(null);
  const [cardPosition, setCardPosition] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [direction, setDirection] = useState(1);

  // Normalize single-step props into the same shape as a steps array.
  const activeSteps = useMemo(() => {
    if (Array.isArray(steps) && steps.length > 0) return steps;
    return [{ targetRef, title, description, position, eyebrow }];
  }, [steps, targetRef, title, description, position, eyebrow]);

  const safeIndex = Math.min(stepIndex, activeSteps.length - 1);
  const current = activeSteps[safeIndex] || {};
  const currentTargetRef = current.targetRef;
  const currentPosition = current.position || position;
  const isFirst = safeIndex === 0;
  const isLast = safeIndex === activeSteps.length - 1;
  const isTour = activeSteps.length > 1;

  // Restart the tour whenever it (re)opens.
  useEffect(() => {
    if (isOpen) {
      setStepIndex(0);
      setDirection(1);
    }
  }, [isOpen]);

  // Measure the current step's target + place the card; keep it pinned.
  useEffect(() => {
    if (!isOpen || !currentTargetRef?.current) return;

    const updatePosition = () => {
      const rect = currentTargetRef.current?.getBoundingClientRect();
      if (!rect) return;
      setTargetRect(rect);

      const cardWidth = cardRef.current?.offsetWidth || 320;
      const cardHeight = cardRef.current?.offsetHeight || 200;
      const padding = 16;

      let top, left;
      switch (currentPosition) {
        case "top":
          top = rect.top - cardHeight - padding;
          left = rect.left + rect.width / 2 - cardWidth / 2;
          break;
        case "left":
          top = rect.top + rect.height / 2 - cardHeight / 2;
          left = rect.left - cardWidth - padding;
          break;
        case "right":
          top = rect.top + rect.height / 2 - cardHeight / 2;
          left = rect.right + padding;
          break;
        case "bottom":
        default:
          top = rect.bottom + padding;
          left = rect.left + rect.width / 2 - cardWidth / 2;
      }

      const maxLeft = window.innerWidth - cardWidth - 16;
      const maxTop = window.innerHeight - cardHeight - 16;
      left = Math.max(16, Math.min(left, maxLeft));
      top = Math.max(16, Math.min(top, maxTop));

      setCardPosition({ top, left });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen, currentTargetRef, currentPosition, safeIndex]);

  const handleNext = () => {
    if (isLast) {
      onClose?.();
      return;
    }
    setDirection(1);
    setStepIndex((i) => i + 1);
  };

  const handleBack = () => {
    setDirection(-1);
    setStepIndex((i) => Math.max(0, i - 1));
  };

  // `targetRect` is retained on close so the exit animation can play.
  const open = isOpen && !!targetRect;

  const highlightStyle = targetRect
    ? {
        top: targetRect.top - 8,
        left: targetRect.left - 8,
        width: targetRect.width + 16,
        height: targetRect.height + 16,
      }
    : null;

  // Card grows out of the target from the correct edge.
  const growOrigin =
    currentPosition === "top"
      ? "bottom center"
      : currentPosition === "left"
        ? "center right"
        : currentPosition === "right"
          ? "center left"
          : "top center";
  const growOffset =
    currentPosition === "top"
      ? { y: 18 }
      : currentPosition === "left"
        ? { x: 18 }
        : currentPosition === "right"
          ? { x: -18 }
          : { y: -18 };

  const cardVariants = prefersReduced
    ? { hidden: { opacity: 0 }, visible: { opacity: 1 } }
    : {
        hidden: { opacity: 0, scale: 0.9, ...growOffset },
        visible: {
          opacity: 1,
          scale: 1,
          x: 0,
          y: 0,
          transition: { type: "spring", stiffness: 260, damping: 26, mass: 0.9 },
        },
      };

  const cardExit = prefersReduced
    ? { opacity: 0, transition: { duration: 0.15 } }
    : {
        opacity: 0,
        scale: 0.96,
        ...growOffset,
        transition: { duration: 0.2, ease: EASE_OUT_EXPO },
      };

  // Per-step content crossfade (slides toward the travel direction).
  const contentVariants = {
    enter: (dir) => ({ opacity: 0, x: prefersReduced ? 0 : dir > 0 ? 16 : -16 }),
    center: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.28, ease: EASE_OUT_EXPO },
    },
    exit: (dir) => ({
      opacity: 0,
      x: prefersReduced ? 0 : dir > 0 ? -16 : 16,
      transition: { duration: 0.16, ease: EASE_OUT_EXPO },
    }),
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Spotlight cut-out: transparent window over the target whose huge
              box-shadow spread darkens everything else, so the target shows
              through at full brightness. Glides smoothly between steps. */}
          <motion.div
            className="pointer-events-none fixed z-40 rounded-xl"
            style={{
              ...highlightStyle,
              transition: `top ${GLIDE}, left ${GLIDE}, width ${GLIDE}, height ${GLIDE}`,
              boxShadow: [
                "0 0 0 2px color-mix(in oklab, var(--sal-brand-500) 95%, transparent)",
                "0 0 0 9px color-mix(in oklab, var(--sal-brand-500) 16%, transparent)",
                "0 0 30px 4px color-mix(in oklab, var(--sal-brand-500) 25%, transparent)",
                "0 0 0 9999px var(--sal-surface-scrim)",
              ].join(", "),
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: EASE_OUT_EXPO }}
          >
            {!prefersReduced &&
              [0, 0.95].map((delay) => (
                <motion.span
                  key={delay}
                  className="absolute inset-0 rounded-xl ring-2 ring-brand-500"
                  animate={{ scale: [1, 1.12], opacity: [0.5, 0] }}
                  transition={{
                    duration: 1.9,
                    ease: "easeOut",
                    repeat: Infinity,
                    delay,
                  }}
                />
              ))}
          </motion.div>

          {/* Announcement card */}
          <motion.div
            ref={cardRef}
            role="dialog"
            aria-modal="false"
            aria-label={current.title}
            className="fixed z-50 w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-gray-200 bg-white p-5 shadow-[0_28px_70px_-28px_rgba(15,23,42,0.5)] ring-1 ring-gray-900/5"
            style={{
              top: cardPosition?.top,
              left: cardPosition?.left,
              transformOrigin: growOrigin,
              transition: `top ${GLIDE}, left ${GLIDE}`,
            }}
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            exit={cardExit}
          >
            {/* Coral hairline accent, clipped to the rounded corners. */}
            <span
              aria-hidden
              className="pointer-events-none absolute inset-x-0 top-0 h-px bg-brand-600"
            />

            {/* One-shot light shimmer on first open. */}
            {!prefersReduced && (
              <motion.span
                aria-hidden
                className="pointer-events-none absolute inset-y-0 left-0 z-20 w-1/3 -skew-x-12 bg-white/30 blur-md"
                initial={{ x: "-160%", opacity: 0 }}
                animate={{ x: "320%", opacity: [0, 0.9, 0] }}
                transition={{ delay: 0.28, duration: 0.95, ease: "easeOut" }}
              />
            )}

            <button
              onClick={() => onClose?.()}
              aria-label="Dismiss"
              className="absolute right-3 top-3 z-30 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>

            {/* Step content (crossfades between steps) */}
            <AnimatePresence mode="wait" custom={direction} initial={false}>
              <motion.div
                key={safeIndex}
                custom={direction}
                variants={contentVariants}
                initial="enter"
                animate="center"
                exit="exit"
              >
                <div className="flex items-center gap-2">
                  <IconTile icon={Sparkles} size="sm" tone="brand" />
                  <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-600">
                    {current.eyebrow || eyebrow}
                  </span>
                </div>

                <h3 className="mt-3 text-base font-semibold tracking-tight text-gray-900">
                  {current.title}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-gray-600">
                  {current.description}
                </p>
              </motion.div>
            </AnimatePresence>

            {/* Footer: progress dots + navigation */}
            <div className="mt-5">
              {isTour && (
                <div className="mb-3 flex items-center gap-1.5">
                  {activeSteps.map((_, i) => (
                    <span
                      key={i}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        i === safeIndex
                          ? "w-5 bg-brand-600"
                          : "w-1.5 bg-gray-200"
                      }`}
                    />
                  ))}
                  <span className="ml-auto text-xs tabular-nums text-gray-400">
                    {safeIndex + 1} / {activeSteps.length}
                  </span>
                </div>
              )}

              <div className="flex items-center gap-2">
                {isTour && !isFirst && (
                  <button
                    onClick={handleBack}
                    className="inline-flex items-center gap-1 rounded-md px-2.5 py-2 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
                  >
                    <ArrowLeft className="h-3.5 w-3.5" />
                    Back
                  </button>
                )}

                <Button
                  onClick={handleNext}
                  className="group relative ml-auto w-full gap-1.5 overflow-hidden font-semibold"
                  style={isTour && !isFirst ? { width: "auto", flex: "1 1 0%" } : undefined}
                >
                  <span className="relative z-10 inline-flex items-center gap-1.5">
                    {isLast ? buttonText : "Next"}
                    {!isLast && <ArrowRight className="h-3.5 w-3.5" />}
                  </span>
                  {!prefersReduced && (
                    <span
                      aria-hidden
                      className="pointer-events-none absolute inset-y-0 left-0 w-1/4 -translate-x-[220%] skew-x-[-20deg] bg-white/20 blur-[1px] transition-transform duration-700 ease-out group-hover:translate-x-[560%]"
                    />
                  )}
                </Button>
              </div>

              {!isTour && (
                <p className="mt-2.5 text-center text-xs text-gray-500">
                  You won&apos;t see this again.
                </p>
              )}
              {isTour && isLast && (
                <p className="mt-2.5 text-center text-xs text-gray-500">
                  You won&apos;t see this again.
                </p>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
